import React, { useCallback, useEffect, useRef } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { WheelGesturesPlugin } from "embla-carousel-wheel-gestures";
import { NextButton, PrevButton, usePrevNextButtons } from "./ArrowButtons";
import { DotButton, useDotButton } from "./DotButton";
import "./Carousel.css";
const TWEEN_FACTOR_BASE = 0.65;

const numberWithinRange = (number, min, max) =>
  Math.min(Math.max(number, min), max);

const Carousel = (props) => {
  const { slides, options, onSelect, exhaustedAgents = {} } = props;
  const wheelGestures = WheelGesturesPlugin();
  const [emblaRef, emblaApi] = useEmblaCarousel(options, [wheelGestures]);
  const tweenFactor = useRef(0);
  const tweenNodes = useRef([]);

  const { selectedIndex, scrollSnaps, onDotButtonClick } =
    useDotButton(emblaApi);

  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = usePrevNextButtons(emblaApi);

  const setTweenFactor = useCallback((emblaApi) => {
    tweenFactor.current = TWEEN_FACTOR_BASE * emblaApi.scrollSnapList().length;
  }, []);

  const setTweenNodes = useCallback((emblaApi) => {
    tweenNodes.current = emblaApi.slideNodes().map((slideNode) => {
      return slideNode.querySelector(".embla__slide__img");
    });
  }, []);

  const tweenOpacity = useCallback((emblaApi, eventName) => {
    const engine = emblaApi.internalEngine();
    const scrollProgress = emblaApi.scrollProgress();
    const slidesInView = emblaApi.slidesInView();
    const isScrollEvent = eventName === "scroll";

    emblaApi.scrollSnapList().forEach((scrollSnap, snapIndex) => {
      let diffToTarget = scrollSnap - scrollProgress;
      const slidesInSnap = engine.slideRegistry[snapIndex];

      slidesInSnap.forEach((slideIndex) => {
        if (isScrollEvent && !slidesInView.includes(slideIndex)) return;

        if (engine.options.loop) {
          engine.slideLooper.loopPoints.forEach((loopItem) => {
            const target = loopItem.target();

            if (slideIndex === loopItem.index && target !== 0) {
              const sign = Math.sign(target);

              if (sign === -1) {
                diffToTarget = scrollSnap - (1 + scrollProgress);
              }
              if (sign === 1) {
                diffToTarget = scrollSnap + (1 - scrollProgress);
              }
            }
          });
        }

        const tweenValue = 1 - Math.abs(diffToTarget * tweenFactor.current);
        const clamp = numberWithinRange(tweenValue, 0, 1).toString();
        const tweenNode = tweenNodes.current[slideIndex];
        emblaApi.slideNodes()[slideIndex].style.opacity = clamp;

        const scale = 0.8 + 0.1 * parseFloat(clamp);
        tweenNode.style.transform = `scale(${scale})`;
      });
    });
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    setTweenFactor(emblaApi);
    setTweenNodes(emblaApi);
    tweenOpacity(emblaApi);

    const onSelectHandler = () => {
      const index = emblaApi.selectedScrollSnap();
      onSelect?.(index);
    };

    emblaApi
      .on("reInit", setTweenFactor)
      .on("reInit", setTweenNodes)
      .on("reInit", tweenOpacity)
      .on("scroll", tweenOpacity)
      .on("slideFocus", tweenOpacity)
      .on("select", onSelectHandler);

    // onSelectHandler();

    return () => {
      emblaApi
        .off("reInit", setTweenFactor)
        .off("reInit", setTweenNodes)
        .off("reInit", tweenOpacity)
        .off("scroll", tweenOpacity)
        .off("slideFocus", tweenOpacity)
        .off("select", onSelectHandler);
    };
  }, [emblaApi, tweenOpacity, setTweenFactor, setTweenNodes, onSelect]);

  return (
    <div className="embla">
      <div className="embla__viewport" ref={emblaRef}>
        <div className="embla__container">
          {slides.map((item, index) => (
            <div
              className={`embla__slide ${
                slides.length === 1 ? "centered-slide" : ""
              }`}
              key={index}
            >
              <div className="embla__slide__img-wrapper">
                <img
                  className="embla__slide__img"
                  src={item.image_url}
                  alt={item.label}
                  style={exhaustedAgents[item.agent_name] ? { filter: "grayscale(1)" } : undefined}
                />
                {exhaustedAgents[item.agent_name] && (
                  <div className="embla__slide__exhausted-overlay">
                    <svg
                      className="embla__slide__exhausted-icon"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M12 2l1.5 4.5L18 8l-4.5 1.5L12 14l-1.5-4.5L6 8l4.5-1.5L12 2z" />
                      <path d="M19 14l.75 2.25L22 17l-2.25.75L19 20l-.75-2.25L16 17l2.25-.75L19 14z" />
                      <path d="M5 17l.5 1.5L7 19l-1.5.5L5 21l-.5-1.5L3 19l1.5-.5L5 17z" />
                    </svg>
                    <span className="embla__slide__exhausted-text">Come back tomorrow</span>
                  </div>
                )}
              </div>
              <div className="embla__description">
                <span className="embla__description__text">
                  {item.label ?? item.description ?? ""}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="embla__controls">
        <div className="embla__buttons">
          <PrevButton onClick={onPrevButtonClick} />
          <NextButton onClick={onNextButtonClick} />
        </div>

        {/* <div className="embla__dots">
          {scrollSnaps.map((_, index) => (
            <DotButton
              key={index}
              onClick={() => onDotButtonClick(index)}
              className={'embla__dot'.concat(
                index === selectedIndex ? ' embla__dot--selected' : ''
              )}
            />
          ))}
        </div> */}
      </div>
    </div>
  );
};

export default Carousel;
